import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Loader2 } from "lucide-react";
import type { JSX } from "react";
import { useCreateMcpGateway } from "@/app/mcp-gateways/api/hooks";
import { useReconcileOnboardingProgress } from "@/app/onboarding/hooks/use-reconcile-onboarding-progress";
import type { Jtbd } from "@/app/onboarding-first-win/api/get-jtbds";
import {
  formatToolkitList,
  OnboardingToolkitChips,
} from "@/app/onboarding-first-win/components/onboarding-toolkit-chips";
import { useOnboardingTelemetry } from "@/app/onboarding-first-win/hooks/use-onboarding-telemetry";

type Props = {
  jtbd: Jtbd | null;
  open: boolean;
  onClose: () => void;
};

export function JtbdDetailDialog({ jtbd, open, onClose }: Props): JSX.Element {
  const reconcileOnboardingProgress = useReconcileOnboardingProgress();
  const { mutateAsync: createGatewayAsync, isPending } = useCreateMcpGateway();
  const { captureOnboardingEvent } = useOnboardingTelemetry();

  const toolkitServices = jtbd?.services ?? [];

  const handlePrimaryAction = async () => {
    if (!jtbd) {
      return;
    }

    try {
      const created = await createGatewayAsync({
        name: jtbd.name,
        auth_type: "arcade" as const,
        description: jtbd.description,
        instructions: jtbd.llm_instructions,
        tool_filter: { allowed_tools: jtbd.tools },
      });
      await reconcileOnboardingProgress(created);
      captureOnboardingEvent("Gateway create dialog submitted", {
        current_step: "jtbd",
        gateway_template: "jtbd",
        jtbd_id: jtbd.id,
      });
      onClose();
    } catch {
      return;
    }
  };

  return (
    <Dialog onOpenChange={(isOpen) => !isOpen && onClose()} open={open}>
      <DialogContent className="flex max-h-[85svh] flex-col gap-0 overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{jtbd?.name}</DialogTitle>
          {jtbd?.description && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {jtbd.description}
            </p>
          )}
        </DialogHeader>

        <div className="mt-4 flex max-h-[min(50vh,20rem)] flex-col gap-5 overflow-y-auto pr-2">
          {toolkitServices.length > 0 && (
            <div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                <span className="font-medium text-foreground">Toolkits</span>
                {" — "}
                {formatToolkitList(toolkitServices)}
              </p>
              <OnboardingToolkitChips
                className="mt-2"
                services={toolkitServices}
              />
            </div>
          )}

          <div>
            <p className="mb-2 font-semibold text-muted-foreground text-xs uppercase tracking-widest">
              LLM INSTRUCTIONS
            </p>
            <div className="rounded-md border border-border bg-muted px-3 py-2 font-mono text-muted-foreground text-xs leading-relaxed">
              {jtbd?.llm_instructions}
            </div>
          </div>

          {jtbd?.example_prompts && jtbd.example_prompts.length > 0 && (
            <div>
              <p className="mb-2 font-semibold text-muted-foreground text-xs uppercase tracking-widest">
                EXAMPLE PROMPTS
              </p>
              <div className="flex flex-col gap-2">
                {jtbd.example_prompts.map((prompt) => (
                  <p
                    className="rounded-md border border-border bg-muted/50 px-3 py-2 text-muted-foreground text-sm italic leading-relaxed"
                    key={`${jtbd.id}-${prompt.slice(0, 32)}`}
                  >
                    {prompt}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mx-0 mt-4 mb-0 flex shrink-0 flex-col gap-2 sm:flex-col sm:justify-stretch">
          <Button
            className="w-full"
            disabled={isPending}
            onClick={handlePrimaryAction}
            type="button"
            variant="default"
          >
            {isPending ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 aria-hidden className="size-4 animate-spin" />
                Saving…
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
